import { useState, useEffect, useCallback, useRef } from 'react';
import type { HandoffNotice } from '../types/chat';
import type { TTSTurnMeta } from '../utils/ttsReport';

export interface BotFeatures {
  screen_share_tab_enabled: boolean;
  screen_share_capture_mode?: 'on_user_turn' | 'every_n_seconds';
  screen_share_interval_seconds?: number;
  /**
   * This call runs the schema engine (any call of a schema-mode bot, the
   * playground). Absent: the bot's normal flow — e.g. a schema-mode bot whose
   * saved schema could not run (session.go BotFeatures).
   */
  schema?: boolean;
  /** The dial page's memory_phone this call keys memory by; absent when the
   *  backend ignored it (not the owner, or the bot's memory is off). */
  memory_phone?: string;
}

export interface ServerMessage {
  type: string;
  transcript?: string;
  response?: string;
  audio?: string;
  error?: string;
  isFinal?: boolean;
  // Helper-only: the full answer and its tool continuations have completed.
  // isFinal alone also closes intermediate tool responses.
  turnComplete?: boolean;
  // Tool-call acknowledgment bubble: a bot bubble, but the tool is still
  // running, so the "working" indicator must stay up until the real answer.
  preamble?: boolean;
  kind?: string; // answer|preamble|processing|filler — utterance kind for styling
  // Helper mode field updates (via function calling)
  fieldId?: string;
  fieldValue?: string;
  // Long-running tool call indicators
  functionName?: string;
  audioMode?: string; // "typing" (keyboard sounds) or "processing" (hold music)
  // Bot features (sent once at connection)
  features?: BotFeatures;
  // Heard-text estimation for interrupted bot turns (type: "cutoff_info")
  heardText?: string;
  // Bot-sent image (type: "bot_image"). The URL is rendered inline in the chat.
  imageUrl?: string;
  imageCaption?: string;
  // Conversation-graph node that produced this bot turn (graph mode only).
  // The sandbox chat uses it to highlight the originating node on the canvas.
  node_id?: string;
  // Terminal handoff notice (type: "handoff").
  handoff_to?: HandoffNotice;
  // Graph-mode runtime variable snapshot (type: "workflow_variables").
  variables?: Record<string, unknown>;
  // Current recorder call ID, advertised when recording is enabled.
  call_id?: string;
  // A spoken bot turn's Deepdub generations (type "tts_meta"), matched to its
  // bubble by tts.display — for "report this word" (utils/ttsReport.ts).
  tts?: TTSTurnMeta;
  // Helper mode: integrations the Sidekick is offering to set up in the chat
  // (type "integration_suggestion"), already filtered to ids we declared.
  integrationIds?: string[];
  integrationReason?: string;
  integrationShowMore?: boolean;
  // Helper mode: voices offered by `set_voice` (type "voice_options"). The tool
  // call named by voiceCallId stays open until a `voice_selection` answers it.
  voiceCallId?: string;
  voices?: Array<{
    id: string;
    name: string;
    description?: string;
    gender?: string;
    language?: string;
    provider?: string;
    yaml?: string;
  }>;
  voiceReason?: string;
  // Exactly one match: apply it without rendering a picker.
  voiceAutoApply?: boolean;
}

export interface ClientMessage {
  type: string;
  audio?: string;
  command?: string;
  text?: string;
  file_data?: string;  // Base64 encoded
  file_type?: string;  // image, audio, document
  mime_type?: string;
  filename?: string;
  timezone?: string;   // IANA timezone from browser (sent with start_recording)
  sample_rate?: number; // Requested TTS sample rate (sent with set_audio_rate)
  mode?: string; // "text" | "voice" (sent with set_mode)
  /** Helper mode: the `field_id` values `update_field` may write (helper_fields).
   *  The schema lives on the client, so the server can only reject a made-up id
   *  once we've told it which ones are real. */
  field_ids?: string[];
  /** Helper mode: integration ids the chat can render a setup card for
   *  (helper_integrations). Same client-side-registry reasoning. */
  integration_ids?: string[];
  /** text_message: seed this into the model's history without asking for a
   *  reply. Used for the Sidekick's priming block when the panel reopens onto
   *  an existing transcript, so it doesn't greet the user all over again. */
  context_only?: boolean;
  // voice_selection: the preset the user picked, resolving an open `set_voice`
  // call. Empty with a `text` means they typed instead of picking.
  preset_id?: string;
  /** schema_config (schema playground voice call): the EXTRACT model for the
   *  whole call, one of the server's allowlist; absent = the bot's AI model
   *  (GET default_extract_model). */
  extract_model?: string;
  /** schema_config: the SPEAK model for the whole call, one of the server's
   *  allowlist; absent = the bot's AI model (GET
   *  default_speak_model_resolved). */
  speak_model?: string;
}

interface UseWebSocketOptions {
  onMessage?: (message: ServerMessage) => void;
  /** `closeCode` is set when the server rejected the socket (4000+): 4029 =
   *  rate/limit/domain, 4404 = the bot does not exist or was deleted. */
  onConnectionFailed?: (closeCode?: number) => void;
  suppressWelcome?: boolean;
  helperMode?: boolean; // Enable helper mode for embedded form assistants
  botIdOverride?: string; // Use this bot ID instead of URL-derived one
  targetBotId?: string; // Target bot being configured (for helper mode)
  /** Helper mode: which LLM to run the Sidekick on (id from sidekickModels.ts).
   *  Honoured by the backend only for helper sessions. */
  sidekickModel?: string;
  authToken?: string; // JWT token to bypass IP rate limits for authenticated bot owners
  embedOrigin?: string; // Origin of the page embedding this iframe (for domain validation)
  textOnly?: boolean; // Open the session in text-only mode (no STT/TTS)
  /** More query params for the WS URL, set last so they win over the ones
   *  above. The schema playground's voice call passes `schema_call=1`. */
  extraParams?: Record<string, string>;
}

const MAX_INITIAL_CONNECT_ATTEMPTS = 3;

export function useWebSocket(options?: UseWebSocketOptions) {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected'>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldAutoReconnect = useRef<boolean>(false);
  const wasEverConnected = useRef<boolean>(false);
  const connectAttempts = useRef<number>(0);
  const onMessageRef = useRef<((message: ServerMessage) => void) | undefined>(options?.onMessage);
  const optionsRef = useRef(options);
  
  // Keep refs up to date
  useEffect(() => {
    onMessageRef.current = options?.onMessage;
    optionsRef.current = options;
  }, [options?.onMessage, options]);

  // `overrides` lets the caller pass values that were just set in the same
  // event handler — useful because optionsRef updates in useEffect (post-render),
  // so synchronous connect() calls would otherwise see stale option values
  // (e.g. a JWT refreshed right before dialing).
  const connect = useCallback((overrides?: { textOnly?: boolean; authToken?: string }) => {
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Don't create a new connection if one already exists or is in progress.
    // This is the single guarantee that we never open two sockets: every
    // connect() path (dial, chat open, chat send, reconnect) funnels here, and
    // the only `new WebSocket` call is below. The check keys off the socket's
    // real readyState (not the laggy React connectionStatus), so concurrent
    // callers can't slip a second socket through.
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        console.warn('connect() ignored — socket already OPEN (reusing it)');
        setConnectionStatus('connected');
        return;
      }
      if (wsRef.current.readyState === WebSocket.CONNECTING) {
        console.warn('connect() ignored — socket already CONNECTING (reusing it)');
        return;
      }
      if (wsRef.current.readyState === WebSocket.CLOSING) {
        // Old WS is still closing (close handshake in progress).
        // Mute ALL its callbacks so leftover frames from the server
        // (welcome chunks, late responses) can't leak into the new
        // session's chat buffer. Then detach it.
        const dying = wsRef.current;
        dying.onmessage = null;
        dying.onerror = null;
        dying.onopen = null;
        dying.onclose = null;
        wsRef.current = null;
      }
    }

    shouldAutoReconnect.current = true;
    connectAttempts.current++;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    // Same-origin, including the port. In production the page is served by
    // the Go backend itself; in dev the rspack server proxies /ws to the Go
    // backend on 8080+offset (rspack.config.ts — HMR was even moved to
    // /hmr-ws to keep this path clear). The old localhost→8080 rewrite here
    // bypassed that proxy and pinned every dev/e2e session to :8080
    // regardless of the port offset the stack was actually running on.
    const port = window.location.port;
    const portSuffix = port ? `:${port}` : '';

    let botId = optionsRef.current?.botIdOverride || '';
    if (!botId) {
      const path = window.location.pathname || '';
      if (path.startsWith('/b/')) {
        botId = path.replace('/b/', '').split('/')[0] || '';
      }
    }
    const wsPath = botId ? `/ws/${botId}` : '/ws';
    
    const params = new URLSearchParams();
    if (optionsRef.current?.helperMode) {
      params.set('helper', 'true');
      if (optionsRef.current?.targetBotId) {
        params.set('target', optionsRef.current.targetBotId);
      }
      if (optionsRef.current?.sidekickModel) {
        params.set('sidekick_model', optionsRef.current.sidekickModel);
      }
    } else if (optionsRef.current?.suppressWelcome) {
      params.set('suppress_welcome', 'true');
    }
    const authTok = overrides?.authToken ?? optionsRef.current?.authToken;
    if (authTok) {
      params.set('auth_token', authTok);
    }
    const textOnlyEffective = overrides?.textOnly ?? optionsRef.current?.textOnly;
    if (textOnlyEffective) {
      params.set('text_only', 'true');
    }
    // Pass the embedder's origin so the backend can validate domain restrictions
    // correctly for widget/headless iframes (whose HTTP Referer is always the platform domain)
    const embedOrigin = optionsRef.current?.embedOrigin || document.referrer || '';
    if (embedOrigin) {
      params.set('embed_origin', embedOrigin);
    }

    // Send persistent browser UID for user memory (stable across sessions)
    if (botId && !optionsRef.current?.helperMode) {
      const uidKey = `voxcore-uid-${botId}`;
      let uid = localStorage.getItem(uidKey);
      if (!uid) {
        uid = crypto.randomUUID();
        localStorage.setItem(uidKey, uid);
      }
      params.set('caller_uid', uid);
    }

    // Forward emulated_caller_id from the page URL into the WS query.
    // Used by the sandbox rail to pretend the call is coming from a
    // chosen phone for eager-lookup testing. The backend honours this
    // only for sandbox bot IDs — see main.go's WS handler.
    try {
      const pageParams = new URLSearchParams(window.location.search);
      const emulated = pageParams.get('emulated_caller_id');
      if (emulated) {
        params.set('emulated_caller_id', emulated);
      }
      // Forward the sandbox "run end-of-call integrations" opt-in too. Backend
      // honours it only for sandbox bot IDs — see main.go's WS handler.
      const runEOC = pageParams.get('run_end_of_call');
      if (runEOC) {
        params.set('run_end_of_call', runEOC);
      }
      const outboundName = pageParams.get('outbound_campaign_name');
      const outboundNumber = pageParams.get('outbound_campaign_number');
      const outboundInstructions = pageParams.get('outbound_campaign_instructions');
      if (outboundName || outboundNumber || outboundInstructions) {
        if (outboundName) params.set('outbound_campaign_name', outboundName);
        if (outboundNumber) params.set('outbound_campaign_number', outboundNumber);
        if (outboundInstructions) params.set('outbound_campaign_instructions', outboundInstructions);
      }
    } catch {
      // window.location.search inaccessible (SSR / unusual context); skip
    }

    const extraParams = optionsRef.current?.extraParams;
    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) params.set(key, value);
    }

    const queryString = params.toString();
    const wsUrl = `${protocol}//${host}${portSuffix}${wsPath}${queryString ? '?' + queryString : ''}`;

    console.log('Connecting to WebSocket:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        wasEverConnected.current = true;
        connectAttempts.current = 0;
        setConnectionStatus('connected');
      };

      ws.onmessage = (event) => {
        try {
          const message: ServerMessage = JSON.parse(event.data);
          if (onMessageRef.current) {
            onMessageRef.current(message);
          }
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event: CloseEvent) => {
        console.log('WebSocket disconnected, code:', event.code);

        // If a newer WS has replaced this one (rapid hangup->dial), ignore
        // this stale close event to avoid clobbering the new connection.
        if (wsRef.current !== null && wsRef.current !== ws) {
          console.log('Ignoring stale onclose from superseded WebSocket');
          return;
        }

        setConnectionStatus('disconnected');
        wsRef.current = null;

        // Application-level close codes (4000+) indicate a deliberate server
        // rejection (rate limit, domain restriction, etc.). Never retry these --
        // the fatal_error message already told the UI what happened.
        if (event.code >= 4000) {
          console.log('Server rejected connection (code %d), not retrying', event.code);
          shouldAutoReconnect.current = false;
          if (optionsRef.current?.onConnectionFailed) {
            optionsRef.current.onConnectionFailed(event.code);
          }
          return;
        }

        if (!shouldAutoReconnect.current) return;

        // Only retry during initial connection (before ever connected).
        // Once connected, the backend cannot resume a session, so mid-session
        // reconnect would just create a confusing new session.
        if (!wasEverConnected.current && connectAttempts.current < MAX_INITIAL_CONNECT_ATTEMPTS) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Reconnect attempt ${connectAttempts.current + 1}/${MAX_INITIAL_CONNECT_ATTEMPTS}...`);
            connect();
          }, 3000);
        } else {
          shouldAutoReconnect.current = false;
          if (wasEverConnected.current) {
            console.log('WebSocket closed after session was active, not reconnecting');
          } else {
            console.error(`Failed to connect after ${MAX_INITIAL_CONNECT_ATTEMPTS} attempts`);
          }
          if (optionsRef.current?.onConnectionFailed) {
            optionsRef.current.onConnectionFailed();
          }
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setConnectionStatus('disconnected');
    }
  }, []);

  const disconnect = useCallback(() => {
    shouldAutoReconnect.current = false;
    wasEverConnected.current = false;
    connectAttempts.current = 0;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      // Don't null wsRef here -- keep it so connect() can see the CLOSING
      // state and onclose can guard against clobbering a newer connection.
    }
    
    setConnectionStatus('disconnected');
    console.log('WebSocket manually disconnected');
  }, []);

  useEffect(() => {
    // Don't auto-connect on mount
    return () => {
      shouldAutoReconnect.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const sendMessage = useCallback((message: ClientMessage) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      //console.log('Sent message:', message);
    } else {
      console.warn('WebSocket is not connected');
    }
  }, []);

  return {
    sendMessage,
    connectionStatus,
    connect,
    disconnect
  };
}
